import { useState, useEffect, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  MagnifyingGlass,
  CircleNotch,
  CheckCircle,
  XCircle,
  CaretDown,
  CaretRight,
  ArrowClockwise,
  User,
  Buildings,
  Clock,
} from '@phosphor-icons/react';
import { platformApi, getPlatformApiErrorMessage } from '@/lib/platformApi';
import { toast } from 'sonner';

// ── Types ────────────────────────────────────────────────────────────────────

interface ProvisioningStep {
  step: string;
  status: 'ok' | 'failed';
  message?: string;
  completedAt: string;
}

interface SessionProvisioning {
  status: string;
  steps: ProvisioningStep[];
  errorMessage?: string;
}

interface SessionSummary {
  id: string;
  token: string;
  email: string;
  firstName: string;
  lastName: string;
  organisationName?: string;
  workspaceType: string;
  plan: string;
  status: string;
  paidAt?: string;
  provisionedAt?: string;
  activatedAt?: string;
  createdAt: string;
  expiresAt: string;
  provisioning?: SessionProvisioning;
}

interface SessionsResponse {
  sessions: SessionSummary[];
  total: number;
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function fmt(str: string | null | undefined) {
  if (!str) return null;
  return new Date(str).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

function fmtTime(str: string | null | undefined) {
  if (!str) return null;
  const d = new Date(str);
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) +
    ' ' + d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
}

// ── Badges ───────────────────────────────────────────────────────────────────

function WorkspaceTypeBadge({ type }: { type: string }) {
  const isSolo = type === 'solo';
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-xs font-medium ${
        isSolo ? 'bg-plat-warning-soft text-plat-warning-text' : 'bg-plat-info-soft text-plat-info-text'
      }`}
    >
      {isSolo ? <User className="h-3 w-3" /> : <Buildings className="h-3 w-3" />}
      {isSolo ? 'Solo' : 'Chambers'}
    </span>
  );
}

function PlanBadge({ plan }: { plan: string }) {
  const map: Record<string, string> = {
    solo: 'bg-plat-warning-soft text-plat-warning-text',
    starter: 'bg-plat-info-soft text-plat-info-text',
    professional: 'bg-plat-indigo-soft text-plat-indigo-text',
    enterprise: 'bg-plat-purple-soft text-plat-purple-text',
  };
  return (
    <span className={`inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium capitalize ${map[plan] ?? 'bg-plat-neutral-soft text-plat-secondary'}`}>
      {plan}
    </span>
  );
}

function SessionStatusBadge({ status }: { status: string }) {
  switch (status) {
    case 'pending_payment':
      return (
        <span className="inline-flex items-center rounded-md bg-plat-neutral-soft px-2 py-0.5 text-xs font-medium text-plat-faint">
          Pending payment
        </span>
      );
    case 'payment_confirmed':
      return (
        <span className="inline-flex items-center gap-1 rounded-md bg-plat-info-soft px-2 py-0.5 text-xs font-medium text-plat-info-text">
          Payment confirmed
        </span>
      );
    case 'provisioning':
      return (
        <span className="inline-flex items-center gap-1 rounded-md bg-plat-info-soft px-2 py-0.5 text-xs font-medium text-plat-info-text">
          <CircleNotch className="h-3 w-3 animate-spin" />
          Provisioning
        </span>
      );
    case 'provisioned':
      return (
        <span className="inline-flex items-center gap-1 rounded-md bg-plat-success-soft px-2 py-0.5 text-xs font-medium text-plat-success-text">
          <CheckCircle className="h-3 w-3" />
          Provisioned
        </span>
      );
    case 'activated':
      return (
        <span className="inline-flex items-center gap-1 rounded-md bg-plat-success-soft px-2 py-0.5 text-xs font-medium text-plat-success-text">
          <CheckCircle className="h-3 w-3" />
          Activated
        </span>
      );
    case 'failed':
      return (
        <span className="inline-flex items-center gap-1 rounded-md bg-plat-danger-soft px-2 py-0.5 text-xs font-medium text-plat-danger-text">
          <XCircle className="h-3 w-3" />
          Failed
        </span>
      );
    case 'expired':
      return (
        <span className="inline-flex items-center gap-1 rounded-md bg-plat-warning-soft px-2 py-0.5 text-xs font-medium text-plat-warning-text">
          <Clock className="h-3 w-3" />
          Expired
        </span>
      );
    default:
      return (
        <span className="inline-flex items-center rounded-md bg-plat-neutral-soft px-2 py-0.5 text-xs font-medium capitalize text-plat-faint">
          {status}
        </span>
      );
  }
}

// ── ProvisioningStepIcon ──────────────────────────────────────────────────────

function StepIcon({ status }: { status: 'ok' | 'failed' }) {
  if (status === 'ok') return <CheckCircle className="h-4 w-4 flex-shrink-0 text-plat-success-text" />;
  return <XCircle className="h-4 w-4 flex-shrink-0 text-plat-danger-text" />;
}

// ── Expandable detail row ─────────────────────────────────────────────────────

function SessionDetailRow({ session }: { session: SessionSummary }) {
  const queryClient = useQueryClient();

  const retryMutation = useMutation({
    mutationFn: async () => {
      await platformApi.post(`/onboarding/${session.id}/retry`, {});
    },
    onSuccess: () => {
      toast.success('Provisioning retry queued');
      queryClient.invalidateQueries({ queryKey: ['platform', 'onboarding-sessions'] });
    },
    onError: (err) => {
      toast.error(getPlatformApiErrorMessage(err, 'Retry failed'));
    },
  });

  // The list endpoint omits provisioning steps for payload size, so fetch the full
  // session on expand — this is what lets support see the step-by-step failure detail.
  const detailQuery = useQuery({
    queryKey: ['platform', 'onboarding-session', session.id],
    queryFn: async () => {
      const res = await platformApi.get(`/onboarding/${session.id}`);
      return (res.data?.data ?? res.data) as SessionSummary;
    },
  });
  const prov = detailQuery.data?.provisioning ?? session.provisioning;
  const steps = prov?.steps ?? [];

  return (
    <tr className="bg-plat-bg">
      <td colSpan={8} className="px-5 py-4">
        <div className="flex items-start gap-6">
          {/* Provisioning steps */}
          <div className="flex-1">
            <div className="mb-2.5 text-xs font-bold uppercase tracking-wider text-plat-muted">
              Provisioning steps
            </div>
            {steps.length > 0 ? (
              <div className="space-y-2">
                {steps.map((step, i) => (
                  <div key={i} className="flex items-start gap-2.5">
                    <StepIcon status={step.status} />
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-plat-secondary">{step.step}</div>
                      {step.message && (
                        <div className="mt-0.5 text-xs text-plat-muted">{step.message}</div>
                      )}
                      <div className="mt-0.5 text-xs text-plat-faint">{fmtTime(step.completedAt)}</div>
                    </div>
                  </div>
                ))}
              </div>
            ) : detailQuery.isLoading ? (
              <div className="flex items-center gap-2 text-sm text-plat-muted">
                <CircleNotch className="h-3.5 w-3.5 animate-spin" /> Loading steps…
              </div>
            ) : (
              <div className="text-sm text-plat-faint">No steps recorded.</div>
            )}

            {prov?.errorMessage && (
              <div className="mt-3 rounded-lg border border-plat-danger-line bg-plat-danger-soft p-3 text-xs text-plat-danger-text">
                <span className="font-medium">Error: </span>
                {prov.errorMessage}
              </div>
            )}
          </div>

          {/* Timestamps */}
          <div className="w-48 flex-shrink-0">
            <div className="mb-2.5 text-xs font-bold uppercase tracking-wider text-plat-muted">
              Timeline
            </div>
            <div className="space-y-1.5 text-xs">
              <div className="flex justify-between gap-4">
                <span className="text-plat-faint">Created</span>
                <span className="text-plat-secondary">{fmt(session.createdAt)}</span>
              </div>
              <div className="flex justify-between gap-4">
                <span className="text-plat-faint">Paid</span>
                <span className={session.paidAt ? 'text-plat-secondary' : 'text-plat-faint'}>
                  {fmt(session.paidAt) ?? '—'}
                </span>
              </div>
              <div className="flex justify-between gap-4">
                <span className="text-plat-faint">Provisioned</span>
                <span className={session.provisionedAt ? 'text-plat-secondary' : 'text-plat-faint'}>
                  {fmt(session.provisionedAt) ?? '—'}
                </span>
              </div>
              <div className="flex justify-between gap-4">
                <span className="text-plat-faint">Activated</span>
                <span className={session.activatedAt ? 'text-plat-success-text' : 'text-plat-faint'}>
                  {fmt(session.activatedAt) ?? '—'}
                </span>
              </div>
              <div className="flex justify-between gap-4">
                <span className="text-plat-faint">Expires</span>
                <span className={new Date(session.expiresAt) < new Date() ? 'text-plat-danger-text' : 'text-plat-secondary'}>
                  {fmt(session.expiresAt)}
                </span>
              </div>
            </div>
          </div>

          {/* Retry button (only for failed) */}
          {session.status === 'failed' && (
            <div className="flex-shrink-0 pt-6">
              <button
                onClick={() => retryMutation.mutate()}
                disabled={retryMutation.isPending}
                className="inline-flex items-center gap-2 rounded-lg border border-plat-warning-line px-3 py-2 text-xs font-medium text-plat-warning-text hover:border-plat-warning-line hover:text-plat-warning-text disabled:cursor-not-allowed disabled:text-plat-disabled"
              >
                {retryMutation.isPending ? (
                  <CircleNotch className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <ArrowClockwise className="h-3.5 w-3.5" />
                )}
                Retry provisioning
              </button>
            </div>
          )}
        </div>
      </td>
    </tr>
  );
}

// ── Filter config ─────────────────────────────────────────────────────────────

const STATUS_OPTIONS = [
  { label: 'All statuses', value: '' },
  { label: 'Pending payment', value: 'pending_payment' },
  { label: 'Payment confirmed', value: 'payment_confirmed' },
  { label: 'Provisioning', value: 'provisioning' },
  { label: 'Provisioned', value: 'provisioned' },
  { label: 'Activated', value: 'activated' },
  { label: 'Failed', value: 'failed' },
  { label: 'Expired', value: 'expired' },
] as const;

const WORKSPACE_OPTIONS = [
  { label: 'All types', value: '' },
  { label: 'Solo', value: 'solo' },
  { label: 'Chambers', value: 'chambers' },
] as const;

// ── OnboardingSessionsPage ────────────────────────────────────────────────────

export default function OnboardingSessionsPage() {
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  // Debounce search
  const debounce = useCallback(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    const cleanup = debounce();
    return cleanup;
  }, [debounce]);

  // Reset page when filters change
  useEffect(() => { setPage(1); }, [debouncedSearch, statusFilter, typeFilter]);

  const { data, isLoading, error } = useQuery<SessionsResponse>({
    queryKey: ['platform', 'onboarding-sessions', statusFilter, typeFilter, debouncedSearch, page],
    queryFn: async () => {
      const res = await platformApi.get('/onboarding', {
        params: {
          status: statusFilter || undefined,
          workspaceType: typeFilter || undefined,
          search: debouncedSearch || undefined,
          page,
          limit: 25,
        },
      });
      return res.data?.data ?? res.data;
    },
  });

  const sessions = data?.sessions ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.ceil(total / 25);

  function toggleExpand(id: string) {
    setExpandedId((prev) => (prev === id ? null : id));
  }

  return (
    <div className="space-y-5">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative min-w-[200px] flex-1 max-w-sm">
          <MagnifyingGlass className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-plat-muted" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search email, name…"
            className="h-9 w-full rounded-lg border border-plat-border bg-plat-surface pl-9 pr-3 text-sm text-plat-fg placeholder-plat-faint focus:border-plat-focus focus:outline-none focus:ring-2 focus:ring-plat-focus/50"
          />
        </div>

        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          className="h-9 rounded-lg border border-plat-border bg-plat-surface px-3 text-sm text-plat-fg focus:border-plat-focus focus:outline-none focus:ring-2 focus:ring-plat-focus/50"
        >
          {WORKSPACE_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>

        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="h-9 rounded-lg border border-plat-border bg-plat-surface px-3 text-sm text-plat-fg focus:border-plat-focus focus:outline-none focus:ring-2 focus:ring-plat-focus/50"
        >
          {STATUS_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>

        {total > 0 && (
          <span className="ml-auto text-xs text-plat-muted">{total} session{total !== 1 ? 's' : ''}</span>
        )}
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="flex items-center justify-center py-20">
          <CircleNotch className="h-7 w-7 animate-spin text-plat-info-text" />
        </div>
      ) : error ? (
        <div className="rounded-xl border border-plat-danger-line bg-plat-danger-soft p-6 text-sm text-plat-danger-text">
          {getPlatformApiErrorMessage(error)}
        </div>
      ) : (
        <>
          <div className="overflow-hidden rounded-xl border border-plat-border bg-plat-surface">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-plat-border">
                  <th className="w-8 px-3 py-3.5" />
                  <th className="px-5 py-3.5 text-xs font-semibold uppercase tracking-[0.06em] text-plat-muted">Email</th>
                  <th className="px-5 py-3.5 text-xs font-semibold uppercase tracking-[0.06em] text-plat-muted">Type</th>
                  <th className="px-5 py-3.5 text-xs font-semibold uppercase tracking-[0.06em] text-plat-muted">Plan</th>
                  <th className="px-5 py-3.5 text-xs font-semibold uppercase tracking-[0.06em] text-plat-muted">Status</th>
                  <th className="px-5 py-3.5 text-xs font-semibold uppercase tracking-[0.06em] text-plat-muted">Created</th>
                  <th className="px-5 py-3.5 text-xs font-semibold uppercase tracking-[0.06em] text-plat-muted">Paid</th>
                  <th className="px-5 py-3.5 text-xs font-semibold uppercase tracking-[0.06em] text-plat-muted">Provisioned</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-plat-border">
                {sessions.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-5 py-10 text-center text-sm text-plat-muted">
                      No onboarding sessions found.
                    </td>
                  </tr>
                ) : sessions.map((s) => {
                  const isExpanded = expandedId === s.id;
                  const displayName = s.organisationName
                    ? s.organisationName
                    : `${s.firstName} ${s.lastName}`;

                  return (
                    <>
                      <tr
                        key={s.id}
                        className={`cursor-pointer transition hover:bg-plat-hover ${isExpanded ? 'bg-plat-border/20' : ''}`}
                        onClick={() => toggleExpand(s.id)}
                      >
                        {/* Expand caret */}
                        <td className="px-3 py-3.5 text-plat-faint">
                          {isExpanded
                            ? <CaretDown className="h-3.5 w-3.5" />
                            : <CaretRight className="h-3.5 w-3.5" />}
                        </td>
                        {/* Email + name */}
                        <td className="px-5 py-3.5">
                          <div className="font-medium text-plat-fg">{displayName}</div>
                          <div className="text-xs text-plat-muted">{s.email}</div>
                        </td>
                        {/* Type */}
                        <td className="px-5 py-3.5">
                          <WorkspaceTypeBadge type={s.workspaceType} />
                        </td>
                        {/* Plan */}
                        <td className="px-5 py-3.5">
                          <PlanBadge plan={s.plan} />
                        </td>
                        {/* Status */}
                        <td className="px-5 py-3.5">
                          <SessionStatusBadge status={s.status} />
                        </td>
                        {/* Created */}
                        <td className="px-5 py-3.5 text-plat-secondary">{fmt(s.createdAt)}</td>
                        {/* Paid */}
                        <td className="px-5 py-3.5">
                          {s.paidAt
                            ? <span className="text-plat-secondary">{fmt(s.paidAt)}</span>
                            : <span className="text-plat-faint">—</span>}
                        </td>
                        {/* Provisioned */}
                        <td className="px-5 py-3.5">
                          {s.provisionedAt
                            ? <span className="text-plat-secondary">{fmt(s.provisionedAt)}</span>
                            : <span className="text-plat-faint">—</span>}
                        </td>
                      </tr>

                      {/* Expanded detail row */}
                      {isExpanded && (
                        <SessionDetailRow key={`${s.id}-detail`} session={s} />
                      )}
                    </>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between text-sm">
              <span className="text-plat-muted">
                Page {page} of {totalPages} — {total} total
              </span>
              <div className="flex gap-2">
                <button
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  className="rounded-lg border border-plat-border px-3 py-1.5 text-xs text-plat-secondary hover:border-plat-border-strong hover:text-plat-fg disabled:cursor-not-allowed disabled:text-plat-disabled"
                >
                  Previous
                </button>
                <button
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => p + 1)}
                  className="rounded-lg border border-plat-border px-3 py-1.5 text-xs text-plat-secondary hover:border-plat-border-strong hover:text-plat-fg disabled:cursor-not-allowed disabled:text-plat-disabled"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
